import { Component } from 'preact'
import { ParentCategoryInfo, WidgetSetting } from './Types'
import TrackingLink from '../link/tracking-link'
import { CurrentUserContext } from '../../server-middleware/userContext'
import Dropdown from '../User/Dropdown'
import classNames from 'classnames'
import Slider from '../slider'
import Icon from '../assets'
import Phrase from '../../libs/translationsUtils/phrase'
import { ModulesContext } from '../../utils/globals'
import MegaMenuLinks from './megaMenuLinks'
import MegaMenuSearch from './megaMenuSearch'
import Search from '../algolia/components/Search'
import AddTopicAnchor from './AddTopicAnchor'
import LoginAnchor from './LoginAnchor'

function isTopicNew() {
  if (typeof window === 'undefined') return null
  return window.location.pathname.match(/^\/topic\/new/)
}

export interface SearchInfoProps extends SearchScope {
  category?: number
  groupId?: number
  isParentCategory?: boolean
}

interface Props {
  items: WidgetSetting[]
  groupCount: number
  communityCategories: ParentCategoryInfo[]
  knowledgeBaseCategories: ParentCategoryInfo[]
  logo: string
  ssoLoginUrl: string
  communityCustomerTitle: string
  newTopicURL: string
  searchInfo: SearchInfoProps
  permissions: {
    ideation: boolean
    productUpdates: boolean
  }
}

interface State {
  extendSearch: boolean
  isMenuOpen: boolean
  enableFocusToSearch: boolean
  items: WidgetSetting[]
}

function shouldShowSearch(): boolean {
  if (typeof window === 'undefined') return false
  return document.querySelector('.searchbar-in-hero,.hero-search') === null && location.pathname !== '/search'
}

class HeaderNavigation extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      extendSearch: false,
      enableFocusToSearch: false,
      isMenuOpen: false,
      items: props.items.filter((item) => item.visibility).filter((item) => this.hasPermissions(item.key))
    }
  }

  toggleMenu = (isOpen: boolean) => {
    const bodyWrapper = window.document.body
    this.setState({ isMenuOpen: isOpen })
    if (isOpen) {
      bodyWrapper.classList.add('body-overlay')
    } else {
      bodyWrapper.classList.remove('body-overlay')
    }
  }

  hasPermissions = (item: string) => {
    if (item === 'productUpdates' || item === 'ideation') {
      return this.props.permissions[item]
    }
    return true
  }

  render() {
    const { ssoLoginUrl, communityCustomerTitle, logo, searchInfo, knowledgeBaseCategories, communityCategories, newTopicURL } =
      this.props
    const { extendSearch, enableFocusToSearch, items } = this.state

    return (
      <CurrentUserContext.Consumer>
        {(user) => (
          <ModulesContext.Consumer>
            {(modules) => (
              <section className='main-navigation--wrapper header-navigation'>
                <div className='main-navigation-sitewidth'>
                  {logo && (
                    <div className='header-navigation_logo-wrapper'>
                      <TrackingLink
                        href='/'
                        target='_self'
                        className='header-navigation_logo-anchor'
                        track={{
                          type: 'Mega Menu click on logo',
                          event_source: 'control'
                        }}
                      >
                        <img
                          className='header-navigation_logo'
                          src={logo}
                          title=''
                          alt={`${communityCustomerTitle} Logo`}
                        />
                      </TrackingLink>
                    </div>
                  )}
                  <div className='header-navigation-items-wrapper'>
                    <div
                      className={classNames('header-navigation-items_and_search', {
                        'search-extended': extendSearch
                      })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <nav role='navigation'>
                          <MegaMenuLinks
                            openOnHover
                            className='header-navigation-items_menu'
                            items={items}
                            user={user}
                            modules={modules}
                            knowledgeBaseCategories={knowledgeBaseCategories}
                            communityCategories={communityCategories}
                          />
                        </nav>
                        <div className='header-navigation-items_hamburger'>
                          <Slider
                            title='Menu'
                            open={this.state.isMenuOpen}
                            onToggle={this.toggleMenu}
                            trigger={<Icon icon='menu' />}
                          >
                            <MegaMenuLinks
                              openOnHover={false}
                              className='header-navigation-items_hamburger-list'
                              items={items}
                              user={user}
                              modules={modules}
                              knowledgeBaseCategories={knowledgeBaseCategories}
                              communityCategories={communityCategories}
                            />
                          </Slider>
                        </div>
                        {logo && (
                          <div className='header-navigation_logo-wrapper is-hidden-L'>
                            <TrackingLink
                              target='_self'
                              href='/'
                              className='header-navigation_logo-anchor'
                              track={{
                                type: 'Mega Menu click on logo',
                                event_source: 'control'
                              }}
                            >
                              <img className='header-navigation_logo' src={logo} title='' alt='Logo' />
                            </TrackingLink>
                          </div>
                        )}
                      </div>
                      {shouldShowSearch() && (
                        <MegaMenuSearch
                          searchInfo={searchInfo}
                          isUserGuest={!!user.id}
                          extendSearch={extendSearch}
                          enableFocusToSearch={enableFocusToSearch}
                          enableExtendedSearch={() => {
                            return this.setState({ extendSearch: true, enableFocusToSearch: true })
                          }}
                          disableExtendedSearch={() => {
                            return this.setState({
                              extendSearch: false,
                              enableFocusToSearch: false
                            })
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <section className='main-navigation--nav-buttons-wrapper' data-view='MainNavigation'>
                    <ul>
                      {!isTopicNew() && (
                        <li className='is-hidden-S'>
                          <Phrase p='main.navigation.add_topic'>
                            {(title) => <AddTopicAnchor title={title} newTopicURL={newTopicURL} />}
                          </Phrase>
                        </li>
                      )}

                      <Phrase m='Common' p='main.navigation.login'>
                        {(loginPhrase) => {
                          if (user.id) {
                            return <Dropdown {...user} size='M' />
                          }
                          return <LoginAnchor title={loginPhrase} ssoLoginUrl={ssoLoginUrl} />
                        }}
                      </Phrase>
                    </ul>
                  </section>
                </div>
                {shouldShowSearch() && (
                  <div className='header-navigation-search_mobile'>
                    <div className={classNames('header-navigation_extendable-search', 'extended', 'is-visible-S')}>
                      <div className='header-navigation_extended-search'>
                        <form method='get' action='/search/index'>
                          <div>
                            <Search
                              {...searchInfo}
                              isUserGuest={!!user.id}
                              enableFocusToSearch={enableFocusToSearch}
                              callbackOnOutsideClick={() => {
                                return this.setState({
                                  extendSearch: false,
                                  enableFocusToSearch: false
                                })
                              }}
                            />
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}
          </ModulesContext.Consumer>
        )}
      </CurrentUserContext.Consumer>
    )
  }
}

export default HeaderNavigation
